import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import { selectEntities } from 'services/entities/selectors';
import { SprayEntityIds, SprayProductId } from 'services/sprayEvents/types';
import { AppState } from 'services/types';
import { Entity } from 'services/entities/types';
import { selectProductsKeyedById } from 'services/products/selectors';
import { addMonths, isAfter, sub } from 'date-fns';
import { normalizeTimezone } from 'utils/date-timezone';

import { SprayProductProps } from './types';

const selectEntityIds = (
    _state: AppState,
    props: SprayProductProps
): SprayEntityIds => props.entityIds;
const selectProductId = (
    _state: AppState,
    props: SprayProductProps
): SprayProductId => props.productId;
const selectDate = (_state: AppState, props: SprayProductProps): string =>
    props.date;

const plantingDateIsString = (
    plantingDate: Entity['plantingDate']
): plantingDate is string => typeof plantingDate === 'string';

const selectSprayProductMinTreeAgeMonths = createDeepEqualSelector(
    selectProductId,
    selectProductsKeyedById,
    (sprayProductId, products) => {
        const product = products[sprayProductId];

        if (product && typeof product.minTreeAgeMonths !== 'undefined') {
            return product.minTreeAgeMonths;
        }

        return null;
    }
);

const selectEntitiesUnderMinTreeAge = createDeepEqualSelector(
    selectEntityIds,
    selectEntities,
    selectDate,
    selectSprayProductMinTreeAgeMonths,
    (entityIds = [], entities, date, minTreeAgeMonths) => {
        const entitiesWithPlantingDates: Entity[] = [];

        for (const entityId of entityIds) {
            const plantingDate = entityId
                ? entities[entityId] && entities[entityId].plantingDate
                : null;

            if (plantingDateIsString(plantingDate)) {
                entitiesWithPlantingDates.push(entities[entityId]);
            }
        }

        const sprayDate = new Date(date);
        const entitiesUnderMinAge: Entity[] = [];
        for (const entity of entitiesWithPlantingDates) {
            if (entity.plantingDate && typeof minTreeAgeMonths === 'number') {
                const plantingDate = new Date(entity.plantingDate);
                const canSprayAfterDate = sub(
                    addMonths(plantingDate, minTreeAgeMonths),
                    { days: 1 }
                );

                // Requires timezone normalization due to daylight savings shift between dates
                if (
                    !isAfter(
                        normalizeTimezone(sprayDate),
                        normalizeTimezone(canSprayAfterDate)
                    )
                ) {
                    entitiesUnderMinAge.push(entity);
                }
            }
        }

        return entitiesUnderMinAge;
    }
);

const makeSelectEntitiesUnderMinTreeAge =
    (): typeof selectEntitiesUnderMinTreeAge => selectEntitiesUnderMinTreeAge;

export {
    selectSprayProductMinTreeAgeMonths,
    selectEntitiesUnderMinTreeAge,
    makeSelectEntitiesUnderMinTreeAge
};
